import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as triviaApi from '../../api/trivia';

// Lakers purple and gold colors
const LAKERS_PURPLE = '#552583';
const LAKERS_GOLD = '#FDB927';

// Category icons/emojis
const categoryIcons = {
  championships: '🏆',
  players: '🏀',
  coaches: '📋',
  history: '📜',
  showtime: '✨',
  shaq_kobe: '🦁',
  modern: '🌟',
};

// Difficulty colors
const difficultyColors = {
  easy: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  hard: 'bg-red-100 text-red-800 border-red-300',
};

export default function LakersTriviaChallengeModal({ onClose, onUnlock }) {
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyUnlocked, setAlreadyUnlocked] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Ref to avoid stale closure in timer callbacks
  const selectedAnswerRef = useRef(selectedAnswer);
  useEffect(() => {
    selectedAnswerRef.current = selectedAnswer;
  }, [selectedAnswer]);

  // Fetch a challenge
  const fetchChallenge = useCallback(async () => {
    setLoading(true);
    setSelectedAnswer(null);
    setResult(null);
    try {
      const res = await triviaApi.getTriviaChallenge();
      const data = res.data || res;

      if (data.already_unlocked) {
        setAlreadyUnlocked(true);
        setTimeout(() => {
          onUnlock?.();
          onClose();
        }, 2000);
        return;
      }

      setChallenge(data);
      setTimeLeft(data.time_limit_seconds);
    } catch (err) {
      console.error('Failed to fetch trivia challenge:', err);
    } finally {
      setLoading(false);
    }
  }, [onUnlock, onClose]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || result) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Auto-submit if time runs out (use ref to avoid stale closure)
          if (!result && selectedAnswerRef.current) {
            handleSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, result]);

  const handleSubmit = async () => {
    if (!selectedAnswer || submitting) return;

    setSubmitting(true);
    try {
      const res = await triviaApi.submitTriviaAnswer(challenge.challenge_id, selectedAnswer);
      const data = res.data || res;
      setResult(data);

      if (data.unlocked) {
        setShowConfetti(true);
        setTimeout(() => {
          onUnlock?.();
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    fetchChallenge();
  };

  // Confetti effect
  const renderConfetti = () => {
    if (!showConfetti) return null;
    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10px',
              animationDelay: `${Math.random() * 2}s`,
              backgroundColor: i % 2 === 0 ? LAKERS_PURPLE : LAKERS_GOLD,
              width: '10px',
              height: '10px',
              borderRadius: i % 3 === 0 ? '50%' : '0',
            }}
          />
        ))}
      </div>
    );
  };

  if (alreadyUnlocked) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Unlocked!</h2>
          <p className="text-gray-600">You've already proven your Lakers knowledge. Advanced Mode is yours!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti-fall 3s ease-out forwards;
        }
      `}</style>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative">
        {renderConfetti()}

        {/* Header - Lakers themed */}
        <div
          className="px-6 py-5 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${LAKERS_PURPLE} 0%, ${LAKERS_PURPLE}dd 100%)` }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20 transform translate-x-8 -translate-y-8"
            style={{ backgroundColor: LAKERS_GOLD }}
          />
          <div className="relative flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="text-2xl">🏀</span>
                Lakers Trivia Challenge
              </h2>
              <p className="text-purple-200 text-sm mt-1">
                Prove you're a true fan to unlock Advanced Mode
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition p-1 hover:bg-white/10 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mb-4"
                style={{ borderColor: `${LAKERS_GOLD} transparent ${LAKERS_GOLD} ${LAKERS_GOLD}` }}
              />
              <p className="text-gray-600">Loading your challenge...</p>
            </div>
          ) : result ? (
            /* Result Screen */
            <div className="text-center py-4">
              {result.correct ? (
                <>
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-green-600 mb-2">Correct!</h3>
                  <p className="text-gray-700 mb-4">{result.message}</p>
                  {result.fun_fact && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 text-left">
                      <p className="text-sm font-semibold text-purple-800 mb-1">Fun Fact:</p>
                      <p className="text-purple-700 text-sm">{result.fun_fact}</p>
                    </div>
                  )}
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold"
                    style={{ backgroundColor: LAKERS_PURPLE }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Advanced Mode Unlocked!
                  </div>
                </>
              ) : (
                <>
                  <div className="text-6xl mb-4">😅</div>
                  <h3 className="text-2xl font-bold text-red-600 mb-2">Not Quite!</h3>
                  <p className="text-gray-700 mb-2">{result.message}</p>
                  <button
                    onClick={handleTryAgain}
                    className="mt-4 px-6 py-2.5 rounded-xl font-semibold text-white transition hover:opacity-90"
                    style={{ backgroundColor: LAKERS_PURPLE }}
                  >
                    Try Another Question
                  </button>
                </>
              )}
            </div>
          ) : challenge ? (
            /* Question Screen */
            <>
              {/* Category and difficulty badges */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: LAKERS_PURPLE }}
                >
                  {categoryIcons[challenge.category] || '🏀'} {challenge.category.replace('_', ' ')}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${difficultyColors[challenge.difficulty]}`}>
                  {challenge.difficulty}
                </span>
                {/* Timer */}
                <div className="ml-auto flex items-center gap-1">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`font-mono font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-gray-700'}`}>
                    {timeLeft}s
                  </span>
                </div>
              </div>

              {/* Question */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{challenge.question}</h3>

              {/* Hint for hard questions */}
              {challenge.hint && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
                  <span className="font-semibold text-amber-800">Hint: </span>
                  <span className="text-amber-700">{challenge.hint}</span>
                </div>
              )}

              {/* Options */}
              <div className="space-y-2 mb-6">
                {challenge.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedAnswer(option)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedAnswer === option
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <span className="font-medium text-gray-800">{option}</span>
                  </button>
                ))}
              </div>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={!selectedAnswer || submitting}
                className="w-full py-3 rounded-xl font-semibold text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: selectedAnswer ? LAKERS_GOLD : '#ccc' }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking...
                  </span>
                ) : (
                  'Lock In Answer'
                )}
              </button>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Failed to load challenge. Please try again.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Answer correctly to unlock Advanced Topic Modeling with full control over clustering algorithms, dimension reduction, and hyperparameters.
          </p>
        </div>
      </div>
    </div>
  );
}
